import React from "react";
import type { PriceConfigType, PriceType } from "../../../../../../../common-deprecated/types/CommonTypes";
import { hideDiscountInfo } from "../../../../../../../common-deprecated/priceUtils";
import PricePromoIcon from "./PricePromoIcon";
import * as Styles from "./styles/CashPrice";
import { BnbLabel } from "../../../../utils/labels";
import {
    formatAEMCashPrice,
    hasDualCurrency,
} from "../../../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { useBnbSelector } from "../../../../redux/store";
import Popover from "../../../../../../../common-deprecated/components/popover/aem/Popover";

export type CashPriceType = {
    price: PriceType;
    priceConfig: PriceConfigType;
    cultureName: string;
    isLarge?: boolean;
    small?: boolean; // used for showing cash price underneath monthly
    showLoader?: boolean; // shows loader when true
    cashPriceDisclaimer?: string | null; // used as popover disclaimer at price
};
const CashPrice = (props: CashPriceType): JSX.Element | null => {
    const { price, priceConfig, cultureName, small, isLarge, showLoader, cashPriceDisclaimer } = props;
    const { highlight, discount, cash, promotions, licenseFee } = price;

    const hasSecondaryPrice = useBnbSelector((state) => hasDualCurrency(state.commonSettings));

    if (!price.cash || !priceConfig.priceEnabled) return null;

    const hideDiscount = hideDiscountInfo(priceConfig);
    if (showLoader)
        return (
            <Styles.Text isLarge={!!isLarge} isSmall={small}>
                <BnbLabel label="priceLoading" fallbackLabel="spinLoading" />
            </Styles.Text>
        );

    // Highlight is also a type of promotion which does not alter the price but should show the text as highlighted.
    if ((highlight || discount !== cash || promotions.length > 0) && !hideDiscount) {
        const cashFormatted = formatAEMCashPrice(cash, cultureName, priceConfig);
        const discountFormatted = formatAEMCashPrice(discount, cultureName, priceConfig, licenseFee);
        return (
            <>
                <Styles.PricesWrapper>
                    <div>
                        <Styles.Text
                            dangerouslySetInnerHTML={{ __html: discountFormatted }}
                            isLarge={!small && (discount !== cash || promotions.length > 0)}
                            isSmall={small}
                        />
                        {promotions.length > 0 && <PricePromoIcon promotions={promotions} />}
                    </div>

                    {!highlight && discount !== cash && (
                        <Styles.TextLineThrough dangerouslySetInnerHTML={{ __html: cashFormatted }} isSmall={!!small} />
                    )}
                </Styles.PricesWrapper>

                {hasSecondaryPrice && (
                    <Styles.PricesWrapper>
                        <div>
                            <Styles.Text
                                dangerouslySetInnerHTML={{
                                    __html: formatAEMCashPrice(discount, cultureName, priceConfig, licenseFee, true),
                                }}
                                isLarge={!small && (discount !== cash || promotions.length > 0)}
                                isSmall={small}
                                mt={2}
                            />

                            {promotions.length > 0 && <PricePromoIcon promotions={promotions} />}
                        </div>

                        {!highlight && discount !== cash && (
                            <Styles.TextLineThrough
                                dangerouslySetInnerHTML={{
                                    __html: formatAEMCashPrice(cash, cultureName, priceConfig, licenseFee, true),
                                }}
                                fontSize="13px"
                                isSmall={!!small}
                            />
                        )}
                    </Styles.PricesWrapper>
                )}
            </>
        );
    }

    return (
        <Styles.PricesWrapper hasSecondaryPrice={hasSecondaryPrice}>
            <Styles.Text
                isLarge={!!isLarge}
                isSmall={small}
                dangerouslySetInnerHTML={{
                    __html: formatAEMCashPrice(
                        hideDiscountInfo(priceConfig) ? discount : cash,
                        cultureName,
                        priceConfig,
                        licenseFee,
                        false,
                    ),
                }}
            />
            {!hasSecondaryPrice && cashPriceDisclaimer && (
                <Popover iconColor="primaryBlack" showOnClick content={cashPriceDisclaimer} useHTML />
            )}
            {/* Dual currency price */}
            {hasSecondaryPrice && (
                <Styles.Text>
                    <Styles.Text
                        isLarge={!!isLarge}
                        isSmall={small}
                        dangerouslySetInnerHTML={{
                            __html: formatAEMCashPrice(
                                hideDiscountInfo(priceConfig) ? discount : cash,
                                cultureName,
                                priceConfig,
                                licenseFee,
                                true,
                            ),
                        }}
                    />
                    {cashPriceDisclaimer && (
                        <Popover iconColor="primaryBlack" showOnClick content={cashPriceDisclaimer} useHTML />
                    )}
                </Styles.Text>
            )}
        </Styles.PricesWrapper>
    );
};

export default CashPrice;
